import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import {
  Box,
  Flex,
  Text,
  Button,
  Badge,
  HStack,
  Heading,
  Switch,
  IconButton,
} from '@chakra-ui/react'
import { FileText, Banknote, Check, Sparkles, Link, Unlink, Circle, CircleDot } from 'lucide-react'
import { useAccountLinesForLettrage, useCreateLettrage } from '../hooks'
import { useAccountingContext } from '../context/AccountingContext'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type { MatchSuggestion, LettrageEntryLine } from '../types'
import { useTranslation } from 'react-i18next'

interface LettrageWorkspaceProps {
  accountId: string
  unitId: string | null
  accountCode: string
  ownerName: string | null
  unitNumber: string | null
}

// Largeur du gutter central pour les lignes de connexion
const GUTTER_WIDTH = 160

// Type de la source active (débit ou crédit)
type SourceType = 'debit' | 'credit' | null

export function LettrageWorkspace({
  accountId,
  unitId,
  accountCode: _accountCode,
  ownerName: _ownerName,
  unitNumber: _unitNumber,
}: LettrageWorkspaceProps) {
  const { t } = useTranslation()
  const { residenceId, activeFiscalYear } = useAccountingContext()
  const [showLettered, setShowLettered] = useState(false)
  // Source active: peut être un débit OU un crédit
  const [activeSourceId, setActiveSourceId] = useState<string | null>(null)
  const [activeSourceType, setActiveSourceType] = useState<SourceType>(null)
  // Map<sourceId, Map<targetId, amount>> pour le lettrage par montant
  // La source peut être un débit (avec cibles = crédits) ou un crédit (avec cibles = débits)
  const [pendingConnections, setPendingConnections] = useState<Map<string, Map<string, number>>>(new Map())

  // Refs pour les positions des lignes
  const debitRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const creditRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const gutterRef = useRef<HTMLDivElement>(null)
  const leftColumnRef = useRef<HTMLDivElement>(null)
  const rightColumnRef = useRef<HTMLDivElement>(null)
  const [linePositions, setLinePositions] = useState<Array<{
    key: string
    sourceId: string
    sourceType: SourceType
    y1: number
    y2: number
    isBalanced: boolean
    isActive: boolean
  }>>([])

  const createLettrage = useCreateLettrage()

  const { data: linesData, isLoading } = useAccountLinesForLettrage(accountId, unitId, {
    residence_id: residenceId,
    fiscal_year_id: activeFiscalYear?.id,
    show_lettered: showLettered,
  })

  // Lignes filtrées pour l'affichage
  const debits = linesData?.debits || []
  const credits = linesData?.credits || []
  // Toutes les lignes pour le calcul des soldes (non affecté par le filtre showLettered)
  const allDebits = linesData?.allDebits || []
  const allCredits = linesData?.allCredits || []

  // Calculer les positions des lignes de connexion
  const updateLinePositions = useCallback(() => {
    if (!gutterRef.current || !leftColumnRef.current || !rightColumnRef.current) return

    const gutterRect = gutterRef.current.getBoundingClientRect()
    const leftScroll = leftColumnRef.current.scrollTop
    const rightScroll = rightColumnRef.current.scrollTop

    const newPositions: typeof linePositions = []

    pendingConnections.forEach((targetAmounts, sourceId) => {
      // Déterminer le type de source en cherchant dans les débits puis les crédits
      const isDebitSource = debits.some(d => d.id === sourceId)
      const sourceType: SourceType = isDebitSource ? 'debit' : 'credit'

      const sourceRefs = sourceType === 'debit' ? debitRefs : creditRefs
      const targetRefs = sourceType === 'debit' ? creditRefs : debitRefs
      const sourceScroll = sourceType === 'debit' ? leftScroll : rightScroll
      const targetScroll = sourceType === 'debit' ? rightScroll : leftScroll

      const sourceEl = sourceRefs.current.get(sourceId)
      if (!sourceEl) return

      const sourceRect = sourceEl.getBoundingClientRect()
      const sourceY = sourceRect.top + sourceRect.height / 2 - gutterRect.top + sourceScroll

      const balance = getSourceBalance(sourceId, sourceType)
      const isBalanced = balance?.isBalanced || false
      const isActive = activeSourceId === sourceId

      targetAmounts.forEach((_, targetId) => {
        const targetEl = targetRefs.current.get(targetId)
        if (!targetEl) return

        const targetRect = targetEl.getBoundingClientRect()
        const targetY = targetRect.top + targetRect.height / 2 - gutterRect.top + targetScroll

        // y1 = gauche (débits), y2 = droite (crédits)
        const y1 = sourceType === 'debit' ? sourceY : targetY
        const y2 = sourceType === 'debit' ? targetY : sourceY

        newPositions.push({
          key: `${sourceId}-${targetId}`,
          sourceId,
          sourceType,
          y1,
          y2,
          isBalanced,
          isActive,
        })
      })
    })

    setLinePositions(newPositions)
  }, [pendingConnections, activeSourceId, debits])

  // Mettre à jour les positions au scroll et au changement de données
  useEffect(() => {
    updateLinePositions()

    const leftCol = leftColumnRef.current
    const rightCol = rightColumnRef.current

    if (leftCol) leftCol.addEventListener('scroll', updateLinePositions)
    if (rightCol) rightCol.addEventListener('scroll', updateLinePositions)

    return () => {
      if (leftCol) leftCol.removeEventListener('scroll', updateLinePositions)
      if (rightCol) rightCol.removeEventListener('scroll', updateLinePositions)
    }
  }, [updateLinePositions, debits, credits])

  // Générer des suggestions de lettrage simples (montants restants identiques)
  const suggestions = useMemo(() => {
    const result: MatchSuggestion[] = []
    const usedCredits = new Set<string>()

    debits
      .filter((d) => d.lettrage_status !== 'fully_lettered' && d.remaining_amount > 0.01)
      .forEach((debit) => {
        const matchingCredit = credits.find(
          (c) =>
            c.lettrage_status !== 'fully_lettered' &&
            c.remaining_amount > 0.01 &&
            !usedCredits.has(c.id) &&
            Math.abs(debit.remaining_amount - c.remaining_amount) < 0.01
        )

        if (matchingCredit) {
          result.push({
            debit_id: debit.id,
            credit_ids: [matchingCredit.id],
            confidence: 95,
            reason: 'Montants restants identiques',
          })
          usedCredits.add(matchingCredit.id)
        }
      })

    return result
  }, [debits, credits])

  // Calculer le solde d'une source avec ses connexions (lettrage par montant)
  // Fonctionne pour source = débit OU crédit
  const getSourceBalance = useCallback(
    (sourceId: string, sourceType: SourceType) => {
      const sourceList = sourceType === 'debit' ? debits : credits
      const source = sourceList.find((s) => s.id === sourceId)
      if (!source) return null

      const targetAmounts = pendingConnections.get(sourceId)
      if (!targetAmounts || targetAmounts.size === 0) {
        return {
          sourceAmount: source.remaining_amount,
          allocatedAmount: 0,
          targetSum: 0,
          remainingSource: source.remaining_amount,
          isBalanced: false,
          canValidate: false,
          connectedCount: 0,
        }
      }

      // Somme des montants alloués aux cibles
      let targetSum = 0
      targetAmounts.forEach((amount) => {
        targetSum += amount
      })

      // Montant restant côté source après allocation
      const remainingSource = source.remaining_amount - targetSum

      // Le lettrage est équilibré si targetSum == sourceAmount (tout est alloué)
      // Le lettrage peut être validé si targetSum > 0 ET targetSum <= sourceAmount
      const isBalanced = Math.abs(remainingSource) < 0.01 && targetSum > 0
      const canValidate = targetSum > 0.01 && remainingSource >= -0.01

      return {
        sourceAmount: source.remaining_amount,
        allocatedAmount: targetSum,
        targetSum,
        remainingSource,
        isBalanced,
        canValidate,
        connectedCount: targetAmounts.size,
      }
    },
    [debits, credits, pendingConnections]
  )

  // Alias pour compatibilité avec le code existant
  const getDebitBalance = useCallback(
    (debitId: string) => getSourceBalance(debitId, 'debit'),
    [getSourceBalance]
  )

  // Gérer le clic sur un débit
  const handleDebitClick = useCallback((debit: LettrageEntryLine) => {
    // Si aucune source active ou si la source est un crédit -> on active ce débit comme source
    if (!activeSourceId || activeSourceType === 'credit') {
      // Si on cliquait déjà sur un crédit source, on ajoute ce débit comme cible
      if (activeSourceId && activeSourceType === 'credit') {
        handleTargetClick(debit, 'debit')
        return
      }
      // Sinon on active ce débit comme nouvelle source
      if (activeSourceId === debit.id && activeSourceType === 'debit') {
        setActiveSourceId(null)
        setActiveSourceType(null)
      } else {
        setActiveSourceId(debit.id)
        setActiveSourceType('debit')
      }
    } else if (activeSourceType === 'debit') {
      // On clique sur un autre débit alors qu'on a déjà un débit sélectionné -> on change de source
      if (activeSourceId === debit.id) {
        setActiveSourceId(null)
        setActiveSourceType(null)
      } else {
        setActiveSourceId(debit.id)
        setActiveSourceType('debit')
      }
    }
  }, [activeSourceId, activeSourceType])

  // Gérer le clic sur un crédit
  const handleCreditClick = useCallback((credit: LettrageEntryLine) => {
    // Si aucune source active ou si la source est un débit -> comportement selon contexte
    if (!activeSourceId || activeSourceType === 'debit') {
      // Si on cliquait sur un débit source, on ajoute ce crédit comme cible
      if (activeSourceId && activeSourceType === 'debit') {
        handleTargetClick(credit, 'credit')
        return
      }
      // Sinon on active ce crédit comme nouvelle source
      if (activeSourceId === credit.id && activeSourceType === 'credit') {
        setActiveSourceId(null)
        setActiveSourceType(null)
      } else {
        setActiveSourceId(credit.id)
        setActiveSourceType('credit')
      }
    } else if (activeSourceType === 'credit') {
      // On clique sur un autre crédit alors qu'on a déjà un crédit sélectionné -> on change de source
      if (activeSourceId === credit.id) {
        setActiveSourceId(null)
        setActiveSourceType(null)
      } else {
        setActiveSourceId(credit.id)
        setActiveSourceType('credit')
      }
    }
  }, [activeSourceId, activeSourceType])

  // Gérer l'ajout/retrait d'une cible (appelé depuis handleDebitClick ou handleCreditClick)
  const handleTargetClick = useCallback(
    (target: LettrageEntryLine, _targetType: 'debit' | 'credit') => {
      if (!activeSourceId || !activeSourceType) return

      const sourceList = activeSourceType === 'debit' ? debits : credits
      const source = sourceList.find(s => s.id === activeSourceId)
      if (!source) return

      setPendingConnections((current) => {
        const updated = new Map(current)
        const targetAmounts = new Map(updated.get(activeSourceId) || new Map())

        if (targetAmounts.has(target.id)) {
          // Retirer la connexion
          targetAmounts.delete(target.id)
        } else {
          // Ajouter avec montant = min(restant source, restant cible)
          const currentAllocated = Array.from(targetAmounts.values()).reduce((s, a) => s + a, 0)
          const remainingSource = source.remaining_amount - currentAllocated
          const amountToAllocate = Math.min(remainingSource, target.remaining_amount)
          targetAmounts.set(target.id, Math.max(0, Math.round(amountToAllocate * 100) / 100))
        }

        updated.set(activeSourceId, targetAmounts)
        return updated
      })
    },
    [activeSourceId, activeSourceType, debits, credits]
  )

  // Annuler toutes les connexions d'une source
  const handleClearConnections = useCallback((sourceId: string) => {
    setPendingConnections((current) => {
      const updated = new Map(current)
      updated.delete(sourceId)
      return updated
    })
    setActiveSourceId(null)
    setActiveSourceType(null)
  }, [])

  // Valider un lettrage par montant (fonctionne avec source = débit ou crédit)
  const handleValidateLettrage = async (sourceId: string, sourceType: SourceType) => {
    const balance = getSourceBalance(sourceId, sourceType)
    if (!balance?.canValidate) return

    const targetAmounts = pendingConnections.get(sourceId)
    if (!targetAmounts || targetAmounts.size === 0) return

    const sourceList = sourceType === 'debit' ? debits : credits
    const source = sourceList.find(s => s.id === sourceId)
    if (!source) return

    // Construire le tableau des montants par ligne
    // Inclut la source et toutes les cibles connectées
    const lineAmounts = [
      // La source avec le total des montants alloués
      { lineId: sourceId, amount: balance.targetSum },
      // Les cibles avec leurs montants
      ...Array.from(targetAmounts.entries()).map(([targetId, amount]) => ({
        lineId: targetId,
        amount,
      })),
    ]

    try {
      await createLettrage.mutateAsync({
        accountId,
        lineAmounts,
      })

      setPendingConnections((current) => {
        const updated = new Map(current)
        updated.delete(sourceId)
        return updated
      })

      setActiveSourceId(null)
      setActiveSourceType(null)

      toaster.create({
        title: t('accounting.lettrage.validated', 'Lettrage validé'),
        type: 'success',
      })
    } catch (error) {
      toaster.create({
        title: t('common.error', 'Erreur'),
        description: error instanceof Error ? error.message : t('accounting.lettrage.error', 'Erreur lors du lettrage'),
        type: 'error',
      })
    }
  }

  // Accepter une suggestion (avec montants par défaut)
  const handleAcceptSuggestion = useCallback((suggestion: MatchSuggestion) => {
    const debit = debits.find(d => d.id === suggestion.debit_id)
    if (!debit) return

    setPendingConnections((current) => {
      const updated = new Map(current)
      const creditAmounts = new Map<string, number>()

      let remainingDebit = debit.remaining_amount

      suggestion.credit_ids.forEach(creditId => {
        const credit = credits.find(c => c.id === creditId)
        if (credit) {
          const amount = Math.min(remainingDebit, credit.remaining_amount)
          creditAmounts.set(creditId, Math.round(amount * 100) / 100)
          remainingDebit -= amount
        }
      })

      updated.set(suggestion.debit_id, creditAmounts)
      return updated
    })
  }, [debits, credits])

  // Accepter toutes les suggestions (avec montants)
  const handleAcceptAllSuggestions = useCallback(() => {
    setPendingConnections((current) => {
      const updated = new Map(current)

      suggestions.forEach((s) => {
        const debit = debits.find(d => d.id === s.debit_id)
        if (!debit) return

        const creditAmounts = new Map<string, number>()
        let remainingDebit = debit.remaining_amount

        s.credit_ids.forEach(creditId => {
          const credit = credits.find(c => c.id === creditId)
          if (credit) {
            const amount = Math.min(remainingDebit, credit.remaining_amount)
            creditAmounts.set(creditId, Math.round(amount * 100) / 100)
            remainingDebit -= amount
          }
        })

        updated.set(s.debit_id, creditAmounts)
      })

      return updated
    })
  }, [suggestions, debits, credits])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    })
  }

  // Calculer les statistiques du compte
  // Convention comptable : Crédit = positif (paiement), Débit = négatif (dette)
  // Solde = Crédits - Débits
  // Si positif = compte créditeur (trop payé), si négatif = compte débiteur (doit encore)
  const stats = useMemo(() => {
    // Solde réel = tous les crédits - tous les débits (TOUTES les lignes, pas juste celles affichées)
    const totalDebit = allDebits.reduce((sum, d) => sum + d.debit, 0)
    const totalCredit = allCredits.reduce((sum, c) => sum + c.credit, 0)
    const realBalance = totalCredit - totalDebit

    return {
      realBalance,
    }
  }, [allDebits, allCredits])

  // Calculer le solde des écritures sélectionnées
  // Convention : Crédit = + (paiement), Débit = - (dette)
  // Solde = Crédits sélectionnés - Débits sélectionnés
  // Si négatif = il manque des crédits pour couvrir les débits
  // Si positif = il y a trop de crédits
  // Si 0 = équilibré
  const selectedBalance = useMemo(() => {
    let totalSelectedDebit = 0
    let totalSelectedCredit = 0
    let hasConnections = false

    pendingConnections.forEach((targetAmounts, sourceId) => {
      if (targetAmounts.size === 0) return
      hasConnections = true

      // Vérifier si la source est un débit ou un crédit
      const isDebitSource = debits.some(d => d.id === sourceId)

      // Somme des montants alloués pour cette source
      let allocatedSum = 0
      targetAmounts.forEach((amount) => {
        allocatedSum += amount
      })

      if (isDebitSource) {
        // Source = débit : le montant total du débit est sélectionné
        // Les cibles (crédits) sont les montants alloués
        const source = debits.find(d => d.id === sourceId)
        if (source) {
          totalSelectedDebit += source.remaining_amount
          totalSelectedCredit += allocatedSum
        }
      } else {
        // Source = crédit : le montant total du crédit est sélectionné
        // Les cibles (débits) sont les montants alloués
        const source = credits.find(c => c.id === sourceId)
        if (source) {
          totalSelectedCredit += source.remaining_amount
          totalSelectedDebit += allocatedSum
        }
      }
    })

    // Solde = Crédits - Débits
    // Négatif = il manque des crédits (ex: 300 crédit - 1023 débit = -723)
    // Positif = trop de crédits
    // Zéro = équilibré
    const value = totalSelectedCredit - totalSelectedDebit

    return { value, hasConnections }
  }, [pendingConnections, debits, credits])

  // Regrouper les connexions par source pour les boutons du gutter
  const gutterButtons = useMemo(() => {
    const buttons: Array<{
      sourceId: string
      sourceType: SourceType
      centerY: number
      canValidate: boolean
    }> = []

    pendingConnections.forEach((targetAmounts, sourceId) => {
      if (targetAmounts.size === 0) return

      // Déterminer le type de source
      const isDebitSource = debits.some(d => d.id === sourceId)
      const sourceType: SourceType = isDebitSource ? 'debit' : 'credit'

      // Trouver les positions Y pour ce groupe
      const relevantLines = linePositions.filter(l => l.sourceId === sourceId)
      if (relevantLines.length === 0) return

      // Calculer le centre Y
      const allYs = relevantLines.flatMap(l => [l.y1, l.y2])
      const minY = Math.min(...allYs)
      const maxY = Math.max(...allYs)
      const centerY = (minY + maxY) / 2

      const balance = getSourceBalance(sourceId, sourceType)

      buttons.push({
        sourceId,
        sourceType,
        centerY,
        canValidate: balance?.canValidate || false,
      })
    })

    return buttons
  }, [pendingConnections, linePositions, getSourceBalance, debits])

  if (isLoading) {
    return (
      <Box p={6} textAlign="center">
        <Text color="gray.500">Chargement...</Text>
      </Box>
    )
  }

  return (
    <Box h="full" display="flex" flexDirection="column">
      {/* En-tête compact */}
      <Box px={4} py={2} borderBottomWidth="1px" bg="gray.50">
        <Flex justify="space-between" align="center">
          <Box>
            {/* Ligne 1: Solde réel du compte */}
            <HStack gap={2}>
              <Text fontSize="sm" color="gray.500">Solde du compte {_accountCode} :</Text>
              <Text
                fontSize="sm"
                fontWeight="bold"
                color={Math.abs(stats.realBalance) < 0.01 ? 'green.600' : stats.realBalance > 0 ? 'orange.600' : 'red.600'}
              >
                {stats.realBalance >= 0 ? '+' : ''}{formatCurrency(stats.realBalance)}
              </Text>
              {suggestions.length > 0 && (
                <Button
                  size="xs"
                  colorPalette="blue"
                  variant="subtle"
                  onClick={handleAcceptAllSuggestions}
                  ml={2}
                >
                  <Sparkles size={12} />
                  {suggestions.length} suggestion{suggestions.length > 1 ? 's' : ''}
                </Button>
              )}
            </HStack>

            {/* Ligne 2: Solde des écritures sélectionnées */}
            {selectedBalance.hasConnections && (
              <HStack gap={2} mt={1}>
                <Text fontSize="sm" color="gray.500">Solde des écritures sélectionnées :</Text>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color={
                    Math.abs(selectedBalance.value) < 0.01
                      ? 'green.600'
                      : selectedBalance.value > 0
                        ? 'orange.600'
                        : 'red.600'
                  }
                >
                  {selectedBalance.value >= 0 ? '+' : ''}{formatCurrency(selectedBalance.value)}
                </Text>
              </HStack>
            )}
          </Box>

          <HStack
            gap={2}
            cursor="pointer"
            onClick={() => setShowLettered(!showLettered)}
          >
            <Switch.Root checked={showLettered} colorPalette="brand" size="sm">
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Root>
            <Text fontSize="xs">Afficher lettrés</Text>
          </HStack>
        </Flex>
      </Box>

      {/* Zone de lettrage avec 3 colonnes */}
      <Flex flex={1} overflow="hidden">
        {/* Colonne des débits */}
        <Box
          ref={leftColumnRef}
          flex={1}
          overflow="auto"
          borderRightWidth="1px"
          borderColor="orange.200"
        >
          <Box px={2} py={1.5} borderBottomWidth="1px" bg="orange.50" position="sticky" top={0} zIndex={1}>
            <HStack gap={2}>
              <FileText size={14} color="var(--chakra-colors-orange-600)" />
              <Heading size="xs" color="orange.700">Débits</Heading>
              <Badge size="sm" colorPalette="orange">{debits.filter((d) => d.lettrage_status !== 'fully_lettered').length}</Badge>
            </HStack>
          </Box>

          {debits
            .filter((d) => showLettered || d.lettrage_status !== 'fully_lettered')
            .map((debit) => {
              const isFullyLettered = debit.lettrage_status === 'fully_lettered'
              const isPartiallyLettered = debit.lettrage_status === 'partially_lettered'
              const isActiveSource = activeSourceId === debit.id && activeSourceType === 'debit'
              const isTargetMode = activeSourceType === 'credit' // Mode cible quand source est un crédit
              const balance = getDebitBalance(debit.id)
              const hasConnections = (balance?.connectedCount || 0) > 0
              const suggestion = suggestions.find((s) => s.debit_id === debit.id)

              // Vérifier si ce débit est connecté comme cible d'un crédit source
              let isConnectedAsTarget = false
              let allocatedAmount = 0
              for (const [srcId, targets] of pendingConnections) {
                if (targets.has(debit.id) && credits.some(c => c.id === srcId)) {
                  isConnectedAsTarget = true
                  allocatedAmount = targets.get(debit.id) || 0
                  break
                }
              }

              const canConnect = isTargetMode && !isFullyLettered && !isConnectedAsTarget && debit.remaining_amount > 0.01

              return (
                <Box
                  key={debit.id}
                  ref={(el: HTMLDivElement | null) => {
                    if (el) debitRefs.current.set(debit.id, el)
                  }}
                  px={2}
                  py={1.5}
                  borderBottomWidth="1px"
                  borderColor="gray.100"
                  bg={isFullyLettered ? 'gray.50' : isActiveSource ? 'orange.100' : isConnectedAsTarget ? 'orange.50' : hasConnections ? (balance?.isBalanced ? 'green.50' : 'orange.50') : isPartiallyLettered ? 'yellow.50' : 'white'}
                  cursor={isFullyLettered ? 'default' : 'pointer'}
                  onClick={() => !isFullyLettered && handleDebitClick(debit)}
                  _hover={!isFullyLettered ? { bg: isActiveSource ? 'orange.100' : 'orange.50' } : undefined}
                  transition="background 0.1s"
                  opacity={isFullyLettered ? 0.5 : (isTargetMode && !canConnect && !isConnectedAsTarget) ? 0.4 : 1}
                >
                  <Flex justify="space-between" align="center" gap={2}>
                    <HStack gap={2} flex={1} minW={0}>
                      {/* Indicateur de statut */}
                      {isPartiallyLettered ? (
                        <CircleDot size={12} color="orange" />
                      ) : isFullyLettered ? (
                        <Check size={12} color="gray" />
                      ) : (
                        <Circle size={12} color="gray" />
                      )}
                      <Text fontSize="xs" color={isFullyLettered ? 'gray.400' : 'gray.500'} fontFamily="mono" flexShrink={0}>
                        {formatDate(debit.transaction_date)}
                      </Text>
                      <Text fontSize="xs" truncate flex={1} color={isFullyLettered ? 'gray.400' : 'gray.600'} fontWeight="medium">
                        {debit.description || debit.label}
                      </Text>
                    </HStack>

                    <HStack gap={1} flexShrink={0}>
                      {/* Codes de lettrage existants */}
                      {debit.lettrage_codes.length > 0 && (
                        <Badge size="sm" colorPalette="gray" variant="subtle" fontSize="2xs">
                          {debit.lettrage_codes.join(', ')}
                        </Badge>
                      )}

                      {!isFullyLettered && suggestion && !hasConnections && (
                        <IconButton
                          size="2xs"
                          variant="ghost"
                          colorPalette="blue"
                          aria-label="Accepter suggestion"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleAcceptSuggestion(suggestion)
                          }}
                        >
                          <Sparkles size={12} />
                        </IconButton>
                      )}

                      {/* Affichage du montant - si lettrage partiel, montant original barré + montant alloué */}
                      {isConnectedAsTarget && allocatedAmount < debit.remaining_amount - 0.01 ? (
                        <Box textAlign="right" w="75px">
                          <Text
                            fontSize="xs"
                            fontFamily="mono"
                            color="gray.400"
                            textDecoration="line-through"
                          >
                            {formatCurrency(debit.remaining_amount)}
                          </Text>
                          <Text
                            fontSize="xs"
                            fontWeight="semibold"
                            fontFamily="mono"
                            color="orange.600"
                          >
                            {formatCurrency(allocatedAmount)}
                          </Text>
                        </Box>
                      ) : (
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          color={isFullyLettered ? 'gray.400' : isConnectedAsTarget ? 'green.600' : isPartiallyLettered ? 'orange.600' : 'orange.700'}
                          fontFamily="mono"
                          w="75px"
                          textAlign="right"
                        >
                          {formatCurrency(isFullyLettered ? debit.debit : isConnectedAsTarget ? allocatedAmount : debit.remaining_amount)}
                        </Text>
                      )}
                    </HStack>
                  </Flex>

                  {/* Info copropriétaire et lot */}
                  <HStack gap={1} mt={0.5}>
                    {debit.unit_number && (
                      <Badge size="sm" variant="outline" colorPalette={isFullyLettered ? 'gray' : 'orange'} fontSize="2xs">
                        Lot {debit.unit_number}
                      </Badge>
                    )}
                    <Text fontSize="2xs" color={isFullyLettered ? 'gray.400' : 'gray.500'} truncate>
                      {debit.owner_name}
                    </Text>
                    {isPartiallyLettered && (
                      <Text fontSize="2xs" color="orange.500">
                        (lettré: {formatCurrency(debit.lettered_amount)})
                      </Text>
                    )}
                  </HStack>
                </Box>
              )
            })}

          {debits.filter((d) => showLettered || d.lettrage_status !== 'fully_lettered').length === 0 && (
            <Box textAlign="center" py={6} color="gray.400">
              <Text fontSize="xs">Aucun débit</Text>
            </Box>
          )}
        </Box>

        {/* Gutter central pour les connexions */}
        <Box
          ref={gutterRef}
          w={`${GUTTER_WIDTH}px`}
          bg="gray.50"
          position="relative"
          overflow="hidden"
          flexShrink={0}
        >
          {/* En-tête du gutter */}
          <Box h="33px" borderBottomWidth="1px" bg="gray.100" display="flex" alignItems="center" justifyContent="center">
            <Link size={14} color="var(--chakra-colors-gray-500)" />
          </Box>

          {/* Lignes de connexion */}
          <svg
            style={{
              position: 'absolute',
              top: 33,
              left: 0,
              width: GUTTER_WIDTH,
              height: 'calc(100% - 33px)',
              pointerEvents: 'none',
            }}
          >
            {linePositions.map(({ key, y1, y2, isBalanced, isActive }) => {
              const startX = 0
              const endX = GUTTER_WIDTH
              const midX = GUTTER_WIDTH / 2

              // Décalage pour tenir compte du header
              const adjustedY1 = y1 - 33
              const adjustedY2 = y2 - 33

              return (
                <g key={key}>
                  {/* Ligne courbe de Bézier */}
                  <path
                    d={`M ${startX} ${adjustedY1} C ${midX} ${adjustedY1}, ${midX} ${adjustedY2}, ${endX} ${adjustedY2}`}
                    fill="none"
                    stroke={isBalanced ? '#22c55e' : '#f97316'}
                    strokeWidth={isActive ? 2.5 : 1.5}
                    strokeDasharray={isBalanced ? 'none' : '4,2'}
                    opacity={isActive ? 1 : 0.6}
                  />
                  {/* Point de départ */}
                  <circle
                    cx={startX}
                    cy={adjustedY1}
                    r={3}
                    fill={isBalanced ? '#22c55e' : '#f97316'}
                    opacity={isActive ? 1 : 0.6}
                  />
                  {/* Point d'arrivée */}
                  <circle
                    cx={endX}
                    cy={adjustedY2}
                    r={3}
                    fill={isBalanced ? '#22c55e' : '#f97316'}
                    opacity={isActive ? 1 : 0.6}
                  />
                </g>
              )
            })}
          </svg>

          {/* Boutons d'action centrés sur les connexions */}
          {gutterButtons.map(({ sourceId, sourceType, centerY, canValidate }) => {
            // Le bouton valider n'est visible que si canValidate ET solde des écritures >= 0
            const canShowValidateButton = canValidate && selectedBalance.value >= -0.01

            return (
              <Box
                key={sourceId}
                position="absolute"
                top={`${centerY}px`}
                left="50%"
                transform="translate(-50%, -50%)"
                zIndex={20}
              >
                <HStack gap={1}>
                  {/* Bouton Valider (vert, cercle) - uniquement si peut valider et solde >= 0 */}
                  {canShowValidateButton && (
                    <IconButton
                      size="sm"
                      colorPalette="green"
                      rounded="full"
                      aria-label={t('accounting.lettrage.validate', 'Valider le lettrage')}
                      onClick={() => handleValidateLettrage(sourceId, sourceType)}
                      loading={createLettrage.isPending}
                      boxShadow="md"
                    >
                      <Check size={16} />
                    </IconButton>
                  )}

                  {/* Bouton Annuler (rouge, plein) - toujours visible */}
                  <IconButton
                    size="sm"
                    colorPalette="red"
                    rounded="full"
                    aria-label={t('accounting.lettrage.cancel', 'Annuler le lettrage')}
                    onClick={() => handleClearConnections(sourceId)}
                    boxShadow="md"
                  >
                    <Unlink size={16} />
                  </IconButton>
                </HStack>
              </Box>
            )
          })}
        </Box>

        {/* Colonne des crédits */}
        <Box
          ref={rightColumnRef}
          flex={1}
          overflow="auto"
          borderLeftWidth="1px"
          borderColor="green.200"
        >
          <Box px={2} py={1.5} borderBottomWidth="1px" bg="green.50" position="sticky" top={0} zIndex={1}>
            <HStack gap={2}>
              <Banknote size={14} color="var(--chakra-colors-green-600)" />
              <Heading size="xs" color="green.700">Crédits</Heading>
              <Badge size="sm" colorPalette="green">{credits.filter((c) => c.lettrage_status !== 'fully_lettered').length}</Badge>
            </HStack>
          </Box>

          {credits
            .filter((c) => showLettered || c.lettrage_status !== 'fully_lettered')
            .map((credit) => {
              const isFullyLettered = credit.lettrage_status === 'fully_lettered'
              const isPartiallyLettered = credit.lettrage_status === 'partially_lettered'
              const isActiveSource = activeSourceId === credit.id && activeSourceType === 'credit'
              const isTargetMode = activeSourceType === 'debit' // Mode cible quand source est un débit

              // Vérifier si ce crédit est connecté comme cible (d'un débit source) et récupérer le montant alloué
              let isConnectedAsTarget = false
              let isConnectedToActiveSource = false
              let connectedSourceBalanced = false
              let allocatedAmount = 0

              for (const [srcId, targetAmounts] of pendingConnections) {
                if (targetAmounts.has(credit.id)) {
                  // C'est connecté comme cible - vérifier si la source est un débit
                  if (debits.some(d => d.id === srcId)) {
                    isConnectedAsTarget = true
                    allocatedAmount = targetAmounts.get(credit.id) || 0
                    if (srcId === activeSourceId) isConnectedToActiveSource = true
                    const balance = getSourceBalance(srcId, 'debit')
                    if (balance?.isBalanced) connectedSourceBalanced = true
                    break
                  }
                }
              }

              // Vérifier aussi si ce crédit est la source d'une connexion (nouveau cas)
              const balanceAsSource = getSourceBalance(credit.id, 'credit')
              const hasConnectionsAsSource = (balanceAsSource?.connectedCount || 0) > 0

              const canConnectAsTarget = isTargetMode && !isFullyLettered && !isConnectedAsTarget && credit.remaining_amount > 0.01

              return (
                <Box
                  key={credit.id}
                  ref={(el: HTMLDivElement | null) => {
                    if (el) creditRefs.current.set(credit.id, el)
                  }}
                  px={2}
                  py={1.5}
                  borderBottomWidth="1px"
                  borderColor="gray.100"
                  bg={isFullyLettered ? 'gray.50' : isActiveSource ? 'green.100' : isConnectedToActiveSource ? 'green.100' : isConnectedAsTarget ? (connectedSourceBalanced ? 'green.50' : 'orange.50') : hasConnectionsAsSource ? (balanceAsSource?.isBalanced ? 'green.50' : 'orange.50') : isPartiallyLettered ? 'yellow.50' : 'white'}
                  cursor={isFullyLettered ? 'default' : 'pointer'}
                  onClick={() => !isFullyLettered && handleCreditClick(credit)}
                  _hover={!isFullyLettered ? { bg: isActiveSource ? 'green.100' : 'green.50' } : undefined}
                  opacity={isFullyLettered ? 0.5 : (isTargetMode && !canConnectAsTarget && !isConnectedAsTarget) ? 0.4 : 1}
                  transition="all 0.1s"
                >
                  <Flex justify="space-between" align="center" gap={2}>
                    <HStack gap={2} flex={1} minW={0}>
                      {/* Indicateur de statut */}
                      {isPartiallyLettered ? (
                        <CircleDot size={12} color="orange" />
                      ) : isFullyLettered ? (
                        <Check size={12} color="gray" />
                      ) : (
                        <Circle size={12} color="gray" />
                      )}
                      <Text fontSize="xs" color={isFullyLettered ? 'gray.400' : 'gray.500'} fontFamily="mono" flexShrink={0}>
                        {formatDate(credit.transaction_date)}
                      </Text>
                      <Text fontSize="xs" truncate flex={1} color={isFullyLettered ? 'gray.400' : 'gray.600'} fontWeight="medium">
                        {credit.description || credit.label}
                      </Text>
                    </HStack>

                    <HStack gap={1} flexShrink={0}>
                      {/* Codes de lettrage existants */}
                      {credit.lettrage_codes.length > 0 && (
                        <Badge size="sm" colorPalette="gray" variant="subtle" fontSize="2xs">
                          {credit.lettrage_codes.join(', ')}
                        </Badge>
                      )}

                      {/* Affichage du montant - si lettrage partiel, montant original barré + montant alloué */}
                      {isConnectedAsTarget && allocatedAmount < credit.remaining_amount - 0.01 ? (
                        <Box textAlign="right" w="75px">
                          <Text
                            fontSize="xs"
                            fontFamily="mono"
                            color="gray.400"
                            textDecoration="line-through"
                          >
                            {formatCurrency(credit.remaining_amount)}
                          </Text>
                          <Text
                            fontSize="xs"
                            fontWeight="semibold"
                            fontFamily="mono"
                            color="green.600"
                          >
                            {formatCurrency(allocatedAmount)}
                          </Text>
                        </Box>
                      ) : (
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          color={isFullyLettered ? 'gray.400' : isConnectedAsTarget ? 'green.600' : isPartiallyLettered ? 'green.600' : 'green.700'}
                          fontFamily="mono"
                          w="75px"
                          textAlign="right"
                        >
                          {formatCurrency(isFullyLettered ? credit.credit : isConnectedAsTarget ? allocatedAmount : credit.remaining_amount)}
                        </Text>
                      )}
                    </HStack>
                  </Flex>

                  {/* Info copropriétaire et lot */}
                  <HStack gap={1} mt={0.5}>
                    {credit.unit_number && (
                      <Badge size="sm" variant="outline" colorPalette={isFullyLettered ? 'gray' : 'green'} fontSize="2xs">
                        Lot {credit.unit_number}
                      </Badge>
                    )}
                    <Text fontSize="2xs" color={isFullyLettered ? 'gray.400' : 'gray.500'} truncate>
                      {credit.owner_name}
                    </Text>
                    {isPartiallyLettered && (
                      <Text fontSize="2xs" color="green.500">
                        (lettré: {formatCurrency(credit.lettered_amount)})
                      </Text>
                    )}
                  </HStack>
                </Box>
              )
            })}

          {credits.filter((c) => showLettered || c.lettrage_status !== 'fully_lettered').length === 0 && (
            <Box textAlign="center" py={6} color="gray.400">
              <Text fontSize="xs">Aucun crédit</Text>
            </Box>
          )}
        </Box>
      </Flex>
    </Box>
  )
}
